import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import {convertGQLStrandToMolBioStrand} from 'constants/MolbioConstants';
import {SequenceType} from 'constants/SequenceConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getUTFlexibleNaturalBasesIfEnabled} from 'modbio/util';
import * as SeqUtil from 'molbio/SeqUtil';
import {showValueMessagingForSharedFile} from 'nav/share/growth/Util';
import {PrimerTableQuery, PrimerTableQueryVariables} from 'toolbar/primer-panel/__generated/PrimerTableQuery';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';

import PrimerRow, {PrimerRowProps} from './PrimerRow';
import SelectedOligoActionsRow, {AddPrimersToField, OligoSchemaFields} from './SelectedOligoActionsRow';

type QueryComponentSequence = NonNullable<PrimerTableQuery['componentSequence']>;

export const PRIMER_TABLE_QUERY = gql`
  query PrimerTableQuery($sequenceId: StrictString!) {
    componentSequence(id: $sequenceId) @client {
      bases @client
      id @client
      name @client
      startIndex @client
      sequencePrimers @client {
        active @client
        bases @client
        checked @client
        displayLabel @client
        displayMeltingTemperature @client
        end @client
        id @client
        isNew @client
        isArchived @client
        modifiedNucleotides @client {
          position @client
          sugarMonomerId @client
          baseMonomerId @client
          phosphateMonomerId @client
        }
        name @client
        nucleotideType @client
        oligoId @client
        partnerSelected @client
        selected @client
        start @client
        strand @client
      }
    }
  }
`;

export interface InnerPrimerTableProps {
  primers: Array<PrimerRowProps>;
  toggleAllChecked: (checked: boolean) => void;
  numArchived: number;
  sequenceName: string;
  oligoSchemaFields: OligoSchemaFields;
  addPrimersToField: AddPrimersToField;
}

export function _filterAndTransformPrimers(
  componentSequence: QueryComponentSequence,
  onPrimerRowClick: PrimerTableProps['onPrimerRowClick'],
  onPrimerToggleCheck: PrimerTableProps['onPrimerToggleCheck']
): InnerPrimerTableProps['primers'] {
  const primersToUse = componentSequence.sequencePrimers.filter(
    // oligoId can be null if both the primer and oligo are new, and haven't been saved
    // to the server yet. We expect all primers to eventually have an oligoId, but
    // we have to filter them out here while waiting for the server response
    (sp) => !sp.isNew && !sp.isArchived && sp.oligoId
  );

  const primers: Array<PrimerRowProps> = primersToUse.map((primer) => ({
    ...primer,
    bases: getUTFlexibleNaturalBasesIfEnabled(
      primer.bases,
      primer.modifiedNucleotides,
      primer.nucleotideType,
      SequenceType.OLIGO
    ),
    checked: Boolean(primer.checked),
    // TS doesn't know we filtered above
    oligoId: primer.oligoId as string,
    meltingTemp: primer.displayMeltingTemperature,
    nucleotideType: primer.nucleotideType,
    position: SeqUtil.modelToDisplay(
      SeqUtil.primerBindPosition(
        convertGQLStrandToMolBioStrand(primer.strand),
        primer.start,
        primer.end,
        componentSequence.bases.length
      ),
      componentSequence.startIndex
    ),
    onPrimerRowClick: (shiftKeySelected: boolean) => {
      onPrimerRowClick(primer.id, shiftKeySelected);
    },
    onPrimerToggleCheck: (checked: boolean) => {
      onPrimerToggleCheck(primer.id, checked);
    },
    displayLabel: primer.displayLabel,
  }));

  return primers;
}

interface PrimerTableWrapperProps {
  onPrimerToggleCheck(primerCid: string, checked: boolean): void;
  onPrimerRowClick(primerCid: string, shiftKeySelected: boolean): void;
  sequenceId: string;
}

export type PrimerTableProps = PrimerTableWrapperProps &
  Omit<InnerPrimerTableProps, 'primers' | 'numArchived'>;

export default function PrimerTable({
  toggleAllChecked,
  sequenceId,
  oligoSchemaFields,
  addPrimersToField,
  onPrimerToggleCheck,
  onPrimerRowClick,
}: PrimerTableProps): JSX.Element {
  return (
    <BenchlingQuery<PrimerTableQuery, PrimerTableQueryVariables>
      query={PRIMER_TABLE_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{sequenceId}}
      fetchPolicy="cache-only"
    >
      {(data) => {
        if (data.componentSequence == null) {
          return null;
        }

        return (
          <PrimerTableContents
            toggleAllChecked={toggleAllChecked}
            oligoSchemaFields={oligoSchemaFields}
            addPrimersToField={addPrimersToField}
            primers={_filterAndTransformPrimers(
              data.componentSequence,
              onPrimerRowClick,
              onPrimerToggleCheck
            )}
            sequenceName={data.componentSequence.name}
            numArchived={data.componentSequence.sequencePrimers.filter((sp) => sp.isArchived).length}
          />
        );
      }}
    </BenchlingQuery>
  );
}

export function PrimerTableContents({
  primers,
  toggleAllChecked,
  numArchived,
  sequenceName,
  oligoSchemaFields,
  addPrimersToField,
}: InnerPrimerTableProps): JSX.Element {
  // Grab all unique oligos (multiple primers might use the same oligo)
  // eslint-disable-next-line no-restricted-syntax
  const primersToExport = _.uniq(
    primers.filter((p) => p.checked),
    false,
    (p) => p.oligoId
  );

  if (primers.length === 0) {
    return (
      <div className="row u-small-spacing-top">
        <div className="col col-md-12">No primers attached to sequence</div>
      </div>
    );
  }

  return (
    <>
      <div className="row u-tiny-spacing-top">
        <div className="col col-md-12">
          <div className="scroll">
            <table className="table table-condensed table-hover primers-table">
              <colgroup>
                <col />
                <col />
                <col />
                <col className="icon" />
              </colgroup>
              <thead>
                <tr>
                  <th>
                    <IndeterminateCheckbox
                      items={primers}
                      selectedMap={(p) => p.checked}
                      disabled={false}
                      onChange={(e) => {
                        toggleAllChecked(e.target.checked);
                      }}
                    />
                  </th>
                  <th>Name</th>
                  <th>Nucleotide Type</th>
                  <th>Position</th>
                  <th>
                    T<sub>m</sub>
                  </th>
                </tr>
              </thead>
              <tbody>
                {primers.map((p, i) => (
                  <PrimerRow key={i} {...p} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {numArchived > 0 && (
        <div className="archived-primers-info u-faded u-italic u-small-spacing-bottom">
          {numArchived} archived {pluralize(numArchived, 'primer')} not shown
        </div>
      )}
      {!showValueMessagingForSharedFile() && (
        <SelectedOligoActionsRow
          oligoSchemaFields={oligoSchemaFields}
          primers={primersToExport}
          sequenceName={sequenceName}
          addPrimersToField={addPrimersToField}
        />
      )}
    </>
  );
}
